import { useMemo } from 'react';
import { useAppSelector } from 'store/hooks';
import { useCachedSelectedProject } from './useCachedSelectedProject';
import { EMPTY_SELECTOR } from './cachedSelectorsUtils';
import {
  BusinessInfoResponse,
  BusinessInsightsRequest,
} from 'store/api/apiTypes';
import { reportApiSlice } from 'store/api/reportApiSlice';

/**
 * useCachedBusinessInsights
 * Selects cached POST /v1/business-insights response (fetched via useBusinessInsights or direct hook usage).
 * Does NOT trigger a network call.
 */
export const useCachedBusinessInsights = () => {
  const { selectedProject } = useCachedSelectedProject();
  const domain = selectedProject?.domain;
  const activeReport = useAppSelector(state => state.history.activeReport);
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );

  const queryArgs = useMemo<BusinessInsightsRequest | null>(
    () => (domain ? { domain, reportId: activeReport?.id } : null),
    [domain, activeReport]
  );

  const selectInsights = useMemo(
    () =>
      queryArgs
        ? reportApiSlice.endpoints.getBusinessInsights.select(queryArgs)
        : (EMPTY_SELECTOR as any),
    [queryArgs]
  );
  const selectResult = useAppSelector(selectInsights as any) as {
    data: BusinessInfoResponse | undefined;
    status: string;
    isLoading?: boolean;
    error?: any;
  };
  const { data, status, isLoading: loading, error } = selectResult;
  const insights = data as BusinessInfoResponse | undefined;
  const isLoaded = insights?.business_profile && !isGenerating && !loading;
  const isLoading = loading || isGenerating;

  return {
    insights,
    companyName: insights?.business_profile?.business_name,
    services: insights?.services || [],
    status,
    isLoading,
    isLoaded,
    error,
    queryArgs,
    hasInsights: !!insights,
  };
};
